import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { detect } from "detect-browser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import InterviewDatePicker from "../../InterviewDatePicker";
import FileInput from "./FileInput";
import Button from "../../shared/button/Button";
import RecruiterActionsList from "./RecruiterActionsList";
import FormSubmitting from "./FormSubmitting";
import {
  CandidateActionsWrapper,
  Form,
  ActionsButton,
  SelectedAction,
  RemoveAction,
  TextArea,
  AttachmentPopupTrigger,
  AttachmentCounter,
  AttachmentPopup,
  Top,
  Bottom,
  CloseButton,
  FinalSalaryTooltip,
  FinalSalaryLabel,
  FinalSalaryInput,
  TextAreaWrapper,
} from "./styles/candidate-actions-styles";

const confirmingInterview = new Set([
  "telephone_interview_confirmed",
  "first_interview_confirmed",
  "second_interview_confirmed",
]);
const showCalendar = (action) => confirmingInterview.has(action);

const textAreaPlaceholder = (selectedAction) =>
  selectedAction && "Enter message here...";

class RecruiterCandidateActions extends Component {
  constructor(props) {
    super(props);

    this.state = {
      textareaFocused: false,
    };
  }

  handleFocus = () => {
    const { textareaFocused } = this.state;
    this.setState({
      textareaFocused: !textareaFocused,
    });
  };

  render() {
    const iOS = detect().name === "ios";

    const {
      action,
      actionDate,
      actionAt,
      actionEndAt,
      actionsDropdown,
      attachmentChange,
      fileInput,
      fileSelected,
      handleDateChange,
      handleStartTimeChange,
      handleEndTimeChange,
      handleMessageChange,
      handleSubmit,
      jobCurrencySymbol,
      jobStatus,
      message,
      offerMade,
      optionHandle,
      removeActionHandle,
      removeAttachment,
      resetButtonFocus,
      selectedAction,
      toggleActionsDropdown,
      handleAttachmentPopup,
      attachmentPopupActive,
      finalSalary,
      handleFinalSalaryInput,
      formSubmitting,
    } = this.props;

    const { textareaFocused } = this.state;

    return (
      <CandidateActionsWrapper
        focused={textareaFocused}
        data-test-id="candidate-actions-wrapper"
      >
        {formSubmitting && <FormSubmitting />}
        <Form action="" onSubmit={handleSubmit}>
          <Top>
            {!selectedAction && (
              <ActionsButton
                onClick={toggleActionsDropdown}
                actionsDropdown
                className="dropdown"
                data-test-id="actions-dropdown"
                selectedAction={selectedAction}
              >
                Add action
                <FontAwesomeIcon icon={faPlusCircle} />
              </ActionsButton>
            )}
            <SelectedAction active={selectedAction}>
              {selectedAction}
              <RemoveAction onClick={removeActionHandle} />
            </SelectedAction>
            <RecruiterActionsList
              actionsDropdown={actionsDropdown}
              optionHandle={optionHandle}
              offerMade={offerMade}
              jobStatus={jobStatus}
            />
            {showCalendar(action) && (
              <InterviewDatePicker
                actionDate={actionDate}
                handleDateChange={handleDateChange}
                actionAt={actionAt}
                handleStartTimeChange={handleStartTimeChange}
                actionEndAt={actionEndAt}
                handleEndTimeChange={handleEndTimeChange}
              />
            )}
          </Top>
          <AttachmentPopupTrigger
            onClick={handleAttachmentPopup}
            textareaFocused={textareaFocused}
          />
          {fileSelected && <AttachmentCounter />}
          <AttachmentPopup active={attachmentPopupActive}>
            <CloseButton onClick={handleAttachmentPopup} />
            <FileInput
              ref={fileInput}
              fileSelected={fileSelected}
              attachmentChange={attachmentChange}
              removeAttachment={removeAttachment}
            />
          </AttachmentPopup>
          {action === "accepted_job" && (
            <Fragment>
              {(!finalSalary || finalSalary === 0) && (
                <FinalSalaryTooltip
                  className="animated fadeInUp"
                  data-test-id="final-salary-tooltip"
                >
                  You must add <strong>final salary</strong> to accept this job
                </FinalSalaryTooltip>
              )}
              <FinalSalaryLabel htmlFor="final-salary">
                Final salary:
              </FinalSalaryLabel>
              <FinalSalaryInput
                type="number"
                data-test-id="final-salary-input"
                id="final-salary"
                name="final-salary"
                placeholder={`Final salary in ${jobCurrencySymbol}`}
                onChange={(e) => handleFinalSalaryInput(e)}
                value={finalSalary || ""}
              />
            </Fragment>
          )}
          <Bottom>
            <TextAreaWrapper focused={textareaFocused}>
              <TextArea
                id="candidate-actions-ta"
                placeholder={textAreaPlaceholder(selectedAction)}
                onChange={handleMessageChange}
                value={message}
                disabled={(!selectedAction || formSubmitting) && "disabled"}
                iOS={iOS}
                focused={textareaFocused}
                onFocus={this.handleFocus}
                onBlur={this.handleFocus}
                actionsActive
              />
            </TextAreaWrapper>
            {action === "accepted_job" ? (
              <Button
                classes="submit-action"
                resetButtonFocus={resetButtonFocus}
                disabled={!selectedAction || !finalSalary || finalSalary === 0}
                textareaFocused={textareaFocused}
                dataTestId="submit-action"
              />
            ) : (
              <Button
                classes="submit-action"
                resetButtonFocus={resetButtonFocus}
                disabled={formSubmitting || !selectedAction}
                textareaFocused={textareaFocused}
                dataTestId="submit-action"
              />
            )}
          </Bottom>
        </Form>
      </CandidateActionsWrapper>
    );
  }
}

RecruiterCandidateActions.propTypes = {
  toggleActionsDropdown: PropTypes.func.isRequired,
  actionsDropdown: PropTypes.bool.isRequired,
  offerMade: PropTypes.bool.isRequired,
  jobCurrencySymbol: PropTypes.string.isRequired,
  jobStatus: PropTypes.string.isRequired,
  action: PropTypes.string.isRequired,
  actionDate: PropTypes.instanceOf(Date),
  actionAt: PropTypes.instanceOf(Date),
  actionEndAt: PropTypes.instanceOf(Date),
  handleSubmit: PropTypes.func.isRequired,
  selectedAction: PropTypes.string.isRequired,
  removeActionHandle: PropTypes.func.isRequired,
  optionHandle: PropTypes.func.isRequired,
  handleMessageChange: PropTypes.func.isRequired,
  fileInput: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  message: PropTypes.string.isRequired,
  fileSelected: PropTypes.bool.isRequired,
  attachmentChange: PropTypes.func.isRequired,
  removeAttachment: PropTypes.func.isRequired,
  resetButtonFocus: PropTypes.bool.isRequired,
  handleDateChange: PropTypes.func.isRequired,
  handleStartTimeChange: PropTypes.func.isRequired,
  handleEndTimeChange: PropTypes.func.isRequired,
  handleAttachmentPopup: PropTypes.func.isRequired,
  attachmentPopupActive: PropTypes.bool.isRequired,
  finalSalary: PropTypes.number,
  handleFinalSalaryInput: PropTypes.func.isRequired,
  formSubmitting: PropTypes.bool.isRequired,
};

RecruiterCandidateActions.defaultProps = {
  actionDate: null,
  actionAt: null,
  actionEndAt: null,
  finalSalary: null,
};

export default RecruiterCandidateActions;
